import { z } from 'zod';

/**
 * Catalog of the LLMs Lexibird offers for translation.
 *
 * Ordered by tier (cheapest first), then by provider (Anthropic, OpenAI,
 * Google, SpaceXAI), which is the order the model picker shows them in.
 * The OpenRouter routing for each id lives in `utils/get-ai-sdk-model.ts`;
 * that file needs the OpenRouter client (and server env), whereas this one
 * is also bundled into the client apps.
 */
export type ModelTier = 1 | 2 | 3;

/**
 * Credits charged per character, by model tier. Every model in a tier costs
 * the same; the rates are set from the tier's token costs.
 * See: https://docs.google.com/spreadsheets/d/130fzBdkcCilB-6eF8okR_rLm4WJGqIvhqHs41dVWnsk/edit?usp=sharing
 */
export const CREDITS_PER_CHARACTER_BY_TIER = {
  1: 2,
  2: 25,
  3: 50,
} as const satisfies Record<ModelTier, number>;

export const MODELS = {
  'anthropic:claude-haiku-5.5': {
    name: 'Claude Haiku 5.5',
    provider: 'Anthropic',
    premium: false,
    tier: 1,
    zeroDataRetention: true,
  },
  'openai:gpt-6-luna': {
    name: 'GPT-6 Luna',
    provider: 'OpenAI',
    premium: false,
    tier: 1,
    zeroDataRetention: true,
  },
  'google:gemini-3.5-flash-lite': {
    name: 'Gemini 3.5 Flash Lite',
    provider: 'Google',
    premium: false,
    tier: 1,
    zeroDataRetention: true,
  },
  'anthropic:claude-sonnet-5.5': {
    name: 'Claude Sonnet 5.5',
    provider: 'Anthropic',
    premium: true,
    tier: 2,
    zeroDataRetention: true,
  },
  'openai:gpt-6.1-sol': {
    name: 'GPT-6.1 Sol',
    provider: 'OpenAI',
    premium: true,
    tier: 2,
    zeroDataRetention: true,
  },
  'google:gemini-3.8-flash': {
    name: 'Gemini 3.8 Flash',
    provider: 'Google',
    premium: true,
    tier: 2,
    zeroDataRetention: true,
  },
  'xai:grok-4.7': {
    name: 'Grok 4.7',
    provider: 'SpaceXAI',
    premium: true,
    tier: 2,
    zeroDataRetention: true,
  },
  'anthropic:claude-opus-5.5': {
    name: 'Claude Opus 5.5',
    provider: 'Anthropic',
    premium: true,
    tier: 3,
    zeroDataRetention: true,
  },
  'google:gemini-4-argon': {
    name: 'Gemini 4 Argon',
    provider: 'Google',
    premium: true,
    tier: 3,
    zeroDataRetention: true,
  },
} satisfies Record<
  string,
  {
    /** Human-readable name shown in the model picker. */
    name: string;
    /** Company that makes the model, shown next to the name. */
    provider: string;
    /**
     * Whether the model is premium. Premium models are more expensive but designed to be more powerful.
     */
    premium: boolean;
    /**
     * Model tier, which also sets its price (`CREDITS_PER_CHARACTER_BY_TIER`).
     *
     * Tier 1 (Lite): Fast and affordable models for everyday translation.
     * Tier 2 (Pro): Reliable models for most translation tasks.
     * Tier 3 (Max): Highest-quality models for complex, important, or professional translation.
     */
    tier: ModelTier;
    /**
     * Whether the model can be served with zero data retention, i.e. OpenRouter
     * has at least one endpoint for it that does not retain prompts.
     *
     * Sourced from OpenRouter's `GET /api/v1/models?zdr=true`. Models are the
     * finest granularity it exposes — it does not publish which of a model's
     * endpoints are the ZDR ones — so this only promises that some endpoint
     * qualifies, not that the providers `utils/get-ai-sdk-model.ts` pins do.
     * Passing `provider.zdr` on the request is what actually enforces it.
     *
     * Re-check when adding or bumping a model: support usually lands some time
     * after a model launches, so a `false` here can go stale.
     */
    zeroDataRetention: boolean;
  }
>;

/** Union of every id in the catalog, e.g. `'openai:gpt-6-luna'`. */
export type ModelIdType = keyof typeof MODELS;

/** Every current model id, in catalog order. */
export const MODEL_IDS = Object.keys(MODELS) as readonly ModelIdType[];

/**
 * "Auto" choices, one per tier (Lite, Pro, Max). Each routes to the model
 * we currently recommend for that tier, so we can swap it without users
 * having to pick again.
 */
export const AUTO_MODELS = {
  'auto:lite': { tier: 1, modelId: 'openai:gpt-6-luna' },
  'auto:pro': { tier: 2, modelId: 'google:gemini-3.8-flash' },
  'auto:max': { tier: 3, modelId: 'anthropic:claude-opus-5.5' },
} as const satisfies Record<string, { tier: ModelTier; modelId: ModelIdType }>;

export type AutoModelIdType = keyof typeof AUTO_MODELS;

export const AUTO_MODEL_IDS = Object.keys(
  AUTO_MODELS,
) as readonly AutoModelIdType[];

/** Anything a user can select: a specific model or an "Auto" choice. */
export type ModelSelectionIdType = ModelIdType | AutoModelIdType;

/**
 * Selection used when the user has not picked one. Kept cheap on purpose;
 * heavier models are an explicit opt-in.
 */
export const DEFAULT_MODEL_SELECTION_ID: AutoModelIdType = 'auto:lite';

export const DEFAULT_TRANSLATION_MODEL_ID: ModelIdType =
  AUTO_MODELS[DEFAULT_MODEL_SELECTION_ID].modelId;

/**
 * Parses a model id coming from a client or from stored settings.
 *
 * Accepts specific model ids and "Auto" choices (`AUTO_MODEL_IDS`). Ids of
 * retired models are remapped to their replacement so users keep a
 * comparable model after we retire one. `null` (what older clients stored
 * for "Auto") becomes `'auto:lite'`, and retired models with no direct
 * replacement become the "Auto" choice of their tier. Ids not listed here
 * fall through to the enum and fail validation; callers decide what to do
 * with that (usually `.catch()`).
 *
 * When retiring a model: remove it from `MODELS`, add its id to the
 * matching `case` below (or its tier's "Auto" group), and update
 * `utils/get-ai-sdk-model.ts`.
 */
export const ModelIdSchema = z.preprocess(
  (val) => {
    switch (val) {
      case 'openai:gpt-5-mini':
      case 'openai:gpt-5.4-mini':
      case 'openai:gpt-5.6-luna': {
        return 'openai:gpt-6-luna';
      }
      case 'openai:gpt-5':
      case 'openai:gpt-5.1':
      case 'openai:gpt-5.2':
      case 'openai:gpt-5.4':
      case 'openai:gpt-5.5':
      case 'openai:gpt-5.6-terra':
      case 'openai:gpt-5.6-sol':
      case 'openai:gpt-6-sol': {
        return 'openai:gpt-6.1-sol';
      }
      case 'xai:grok-4':
      case 'xai:grok-4.20-non-reasoning':
      case 'xai:grok-4.3':
      case 'xai:grok-4.5':
      case 'xai:grok-4.6': {
        return 'xai:grok-4.7';
      }
      case 'google:gemini-2.5-pro':
      case 'google:gemini-3-pro':
      case 'google:gemini-3.1-pro':
      case 'google:gemini-2.5-flash':
      case 'google:gemini-3-flash':
      case 'google:gemini-3.5-flash':
      case 'google:gemini-3.6-flash':
      case 'google:gemini-3.7-flash': {
        return 'google:gemini-3.8-flash';
      }
      case 'google:gemini-3.1-flash-lite': {
        return 'google:gemini-3.5-flash-lite';
      }
      case 'anthropic:claude-sonnet-4.5':
      case 'anthropic:claude-sonnet-4.6':
      case 'anthropic:claude-sonnet-5': {
        return 'anthropic:claude-sonnet-5.5';
      }
      case 'anthropic:claude-opus-4.7':
      case 'anthropic:claude-opus-4.8':
      case 'anthropic:claude-opus-5':
      case 'anthropic:claude-fable-5':
      case 'anthropic:claude-fable-5.1': {
        return 'anthropic:claude-opus-5.5';
      }
      case null:
      case 'xai:grok-4-fast-non-reasoning':
      case 'xai:grok-4.1-fast-non-reasoning':
      case 'openai:gpt-5.4-nano': {
        return 'auto:lite';
      }
      case 'openai:gpt-6-astra': {
        return 'auto:max';
      }
      default:
        return val;
    }
  },
  z.enum([...MODEL_IDS, ...AUTO_MODEL_IDS]),
);
